// src/components/AdminLogin.tsx
import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAdminAuth } from '../context/AdminAuthContext';
import { axiosInstance } from '../utils/axiosConfig';
import { validateEmail, validatePassword, collectErrors } from '../utils/validation';
import '../styles/AdminLogin.css';

const AdminLogin: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { adminLogin } = useAdminAuth();

  // Initialize CSRF token before the form POST
  useEffect(() => {
    axiosInstance.get('/health').catch(() => {});
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    const { ok, firstMessage } = collectErrors({
      email: validateEmail(email),
      password: validatePassword(password),
    });
    if (!ok) {
      setError(firstMessage || 'Please enter a valid email and password.');
      return;
    }

    setLoading(true);

    try {
      const response = await axiosInstance.post('/admin/login', {
        email: email.trim(),
        password,
      });

      const { token, admin } = response.data;
      adminLogin(token, admin);
      navigate('/admin');
    } catch (err: any) {
      if (err.response?.status === 403) {
        setError('Access denied. Invalid credentials.');
      } else if (err.response?.status === 400) {
        setError('Please enter both email and password.');
      } else {
        setError('Server error. Please try again later.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-login-page">
      <div className="admin-login-container">
        <div className="admin-login-header">
          <div className="admin-shield-icon">🛡️</div>
          <h1>Admin Portal</h1>
          <p>Sumbitrbuddy Administration</p>
        </div>

        <form onSubmit={handleSubmit} className="admin-login-form">
          {error && (
            <div className="admin-login-error">
              <span className="error-icon">⚠️</span>
              {error}
            </div>
          )}

          <div className="admin-form-group">
            <label htmlFor="admin-email">Email Address</label>
            <input
              id="admin-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@sumbitrbuddy.com"
              required
              autoComplete="email"
              disabled={loading}
            />
          </div>

          <div className="admin-form-group">
            <label htmlFor="admin-password">Password</label>
            <input
              id="admin-password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••"
              required
              autoComplete="current-password"
              disabled={loading}
            />
          </div>

          <button
            type="submit"
            className="admin-login-btn"
            disabled={loading || !email || !password}
          >
            {loading ? (
              <span className="admin-spinner"></span>
            ) : (
              'Sign In to Admin'
            )}
          </button>
        </form>

        <div className="admin-login-footer">
          <p>This portal is restricted to authorized administrators.</p>
          <button
            className="admin-back-link"
            onClick={() => navigate('/login')}
            type="button"
          >
            ← Back to User Login
          </button>
        </div>
      </div>
    </div>
  );
};

export default AdminLogin;
